/**
 * Cloud Operations — Cloud Accounts.
 *
 * List + bind + verify + revoke cloud-account credentials. v1 implements the
 * GCP Workload Identity Federation binding flow; AWS and Azure shown but
 * disabled in the picker until their verifiers land (W3.x).
 *
 * Per design doc §3.1 (data model) + §5 (API). Per memory feedback_v2_inline_over_modal,
 * "Add cloud account" opens an anchored SLIDEOUT, not a modal overlay.
 */

import React, { useEffect, useState } from 'react';
import {
  listCloudAccounts,
  createCloudAccount,
  updateCloudAccount,
  verifyCloudAccount,
  revokeCloudAccount,
} from '../../services/cloudOperationsApi';

const T = {
  ink:        '#0a1d44',
  ink2:       '#0e3552',
  text:       '#1a1f2b',
  text2:      '#5b6b7e',
  text3:      '#8896a8',
  stroke:     '#d4dbe5',
  stroke2:    '#e8edf3',
  bgCard:     '#ffffff',
  bgTint:     '#f7f9fc',
  bgAlt:      '#eef2f8',
  success:    '#2e7d4f',
  successBg:  '#e7f4ec',
  warn:       '#c58b00',
  warnBg:     '#fff4d9',
  danger:     '#c0392b',
  dangerBg:   '#fbe9e7',
  info:       '#1f5fa6',
  infoBg:     '#e8f0fb',
  action:     '#0e3552',
};

export default function CloudAccountsPage() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [slideout, setSlideout] = useState(null); // 'create' | { verify: row } | null
  const [verifyResult, setVerifyResult] = useState(null);

  function reload() {
    setLoading(true);
    listCloudAccounts({ size: 50 })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(e?.message || String(e)); setLoading(false); });
  }
  useEffect(reload, []);

  async function onVerify(row) {
    try {
      const r = await verifyCloudAccount(row.id);
      setVerifyResult(r);
      reload();
    } catch (e) {
      setVerifyResult({ ok: false, reason: e?.message || String(e) });
    }
  }

  async function onRevoke(row) {
    if (!window.confirm(`Revoke cloud account "${row.name}"? This is irreversible.`)) return;
    try {
      await revokeCloudAccount(row.id);
      reload();
    } catch (e) {
      alert('Revoke failed: ' + (e?.message || e));
    }
  }

  return (
    <div>
      <ActionsBar onAdd={() => setSlideout('create')} />
      {error   && <Banner color={T.danger} bg={T.dangerBg}>Could not load: {error}</Banner>}
      {loading && <Banner color={T.info}   bg={T.infoBg}>Loading…</Banner>}
      {!loading && <AccountTable rows={(data && data.items) || []} onVerify={onVerify} onRevoke={onRevoke} onEdit={(row) => setSlideout({ edit: row })} />}
      {verifyResult && (
        <VerifyResultBanner result={verifyResult} onClose={() => setVerifyResult(null)} />
      )}
      <Slideout open={slideout === 'create' || !!(slideout && slideout.edit)} onClose={() => setSlideout(null)}>
        <CreateForm
          editRow={slideout && slideout.edit ? slideout.edit : null}
          onClose={() => setSlideout(null)}
          onSaved={(verifyResult) => { setSlideout(null); if (verifyResult) setVerifyResult(verifyResult); reload(); }}
        />
      </Slideout>
    </div>
  );
}

// ─── List ────────────────────────────────────────────────────────────────────
function ActionsBar({ onAdd }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
      <div style={{ color: T.text2, fontSize: 13 }}>
        Cloud-account credential bindings used by System Deployment Workflows.
        Holds OIDC trust metadata only — never secret material.
      </div>
      <div style={{ flex: 1 }} />
      <button onClick={onAdd} style={btnPrimary}>+ Add cloud account</button>
    </div>
  );
}

function AccountTable({ rows, onVerify, onRevoke, onEdit }) {
  if (rows.length === 0) {
    return (
      <div style={{
        background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6,
        padding: 40, textAlign: 'center', color: T.text3, fontStyle: 'italic',
      }}>
        No cloud accounts bound yet. Click <strong>Add cloud account</strong> to bind one
        — required before creating a Deployment Target.
      </div>
    );
  }
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.bgTint }}>
            <Th>Account</Th>
            <Th>Provider</Th>
            <Th>Identifier</Th>
            <Th>Auth method</Th>
            <Th>Status</Th>
            <Th>Last verified</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: `1px solid ${T.stroke2}` }}>
              <Td>
                <div style={{ fontWeight: 600, color: T.ink }}>{r.name}</div>
                {r.description && <div style={{ color: T.text3, fontSize: 11 }}>{r.description}</div>}
              </Td>
              <Td><ProviderPill provider={r.provider} /></Td>
              <Td><code style={{ fontSize: 11 }}>{r.account_identifier}</code></Td>
              <Td><code style={{ fontSize: 11 }}>{r.auth_method}</code></Td>
              <Td><StatusPill status={r.status} /></Td>
              <Td>{r.last_verified_at
                ? new Date(r.last_verified_at).toLocaleString()
                : <span style={{ color: T.text3 }}>never</span>}</Td>
              <Td>
                <button onClick={() => onEdit(r)} style={btnSm}>Edit</button>{' '}
                <button onClick={() => onVerify(r)} style={btnSm}>Verify</button>{' '}
                <button onClick={() => onRevoke(r)} style={btnSmDanger}>Revoke</button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Create / edit form (slideout body) ──────────────────────────────────────
// editRow present → edit mode: provider/auth_method/identifier/customer are fixed
// (the binding's identity), only name/description/auth_config are editable. On save
// it updates then immediately re-verifies so the operator sees the outcome at once.
function CreateForm({ onClose, onSaved, editRow = null }) {
  const isEdit = !!editRow;
  const ac = (editRow && editRow.auth_config) || {};
  const [provider, setProvider]                = useState(editRow?.provider || 'gcp');
  const [authMethod, setAuthMethod]            = useState(editRow?.auth_method || 'wif');
  const [customerId, setCustomerId]            = useState('');
  const [name, setName]                        = useState(editRow?.name || '');
  const [description, setDescription]          = useState(editRow?.description || '');
  const [accountId, setAccountId]              = useState(editRow?.account_identifier || '');
  // GCP WIF-specific fields (prefilled from the existing auth_config in edit mode)
  const [poolId, setPoolId]                    = useState(ac.workload_identity_pool_id || 'prove7-wif-pool');
  const [providerId, setProviderId]            = useState(ac.workload_identity_provider || 'prove7-wif-github');
  const [projectNumber, setProjectNumber]      = useState(ac.project_number || '');
  const [deploySa, setDeploySa]                = useState(ac.deploy_service_account_email || '');
  const [audience, setAudience]                = useState(ac.audience || '');
  const [submitting, setSubmitting]            = useState(false);
  const [err, setErr]                          = useState(null);

  // Auto-compute audience as the operator types — surfaces format errors live.
  useEffect(() => {
    if (provider === 'gcp' && authMethod === 'wif' && projectNumber && poolId && providerId) {
      setAudience(`//iam.googleapis.com/projects/${projectNumber}/locations/global/workloadIdentityPools/${poolId}/providers/${providerId}`);
    }
  }, [provider, authMethod, projectNumber, poolId, providerId]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const authConfig = provider === 'gcp' && authMethod === 'wif'
        ? {
            workload_identity_pool_id:    poolId,
            workload_identity_provider:   providerId,
            audience,
            deploy_service_account_email: deploySa,
            project_number:               projectNumber,
          }
        : {};
      if (isEdit) {
        // Edit only touches the mutable trust metadata; identity fields are fixed.
        await updateCloudAccount(editRow.id, { name, description, authConfig });
        // Re-verify immediately so the operator sees active/error without a second click.
        let verifyResult = null;
        try { verifyResult = await verifyCloudAccount(editRow.id); }
        catch (ve) { verifyResult = { ok: false, reason: ve?.message || String(ve) }; }
        onSaved(verifyResult);
        return;
      }
      const payload = {
        customerId: customerId || null,
        name, description,
        provider,
        accountIdentifier: accountId,
        authMethod,
        authConfig,
      };
      await createCloudAccount(payload);
      onSaved();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, height: '100%', overflow: 'auto' }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, color: T.ink, fontWeight: 700 }}>
          {isEdit ? 'Edit cloud account' : 'Add cloud account'}
        </h2>
        <p style={{ color: T.text2, fontSize: 12, marginTop: 4 }}>
          {isEdit
            ? 'Complete or correct the OIDC trust metadata, then save to re-verify. The provider, identifier and auth method are fixed for this binding.'
            : 'Binds an OIDC trust between Prove7 and a cloud account. No secret material is stored — runtime auth mints short-lived tokens via STS.'}
        </p>
      </div>

      <Field label="Name" required>
        <input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Prove7 GCP — Acme" required />
      </Field>
      <Field label="Description" hint="Free-text; surfaced in the list view.">
        <textarea style={{ ...inp, height: 60 }} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      {!isEdit && (
        <Field label="Customer ID (UUID)" required hint="The customer this account belongs to.">
          <input style={inp} value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="customer uuid" required />
        </Field>
      )}

      <Field label="Provider" required>
        <select style={inp} value={provider} disabled={isEdit} onChange={(e) => {
          setProvider(e.target.value);
          if (e.target.value === 'gcp')   setAuthMethod('wif');
          if (e.target.value === 'aws')   setAuthMethod('iam_role_oidc');
          if (e.target.value === 'azure') setAuthMethod('azure_workload_identity');
        }}>
          <option value="gcp">GCP</option>
          <option value="aws" disabled>AWS — verifier ships W3.x</option>
          <option value="azure" disabled>Azure — verifier ships W3.x</option>
        </select>
      </Field>

      <Field label="Auth method" required>
        <select style={inp} value={authMethod} disabled={isEdit} onChange={(e) => setAuthMethod(e.target.value)}>
          {provider === 'gcp' && <option value="wif">Workload Identity Federation (recommended)</option>}
          {provider === 'aws' && <option value="iam_role_oidc">IAM Role + OIDC trust</option>}
          {provider === 'azure' && <option value="azure_workload_identity">Azure Workload Identity</option>}
          <option value="bridge_delegated">Bridge-delegated (Self-Hosted only)</option>
        </select>
      </Field>

      <Field label="Account identifier" required hint={
        provider === 'gcp'   ? 'GCP project ID, e.g. prove7-acme-prod' :
        provider === 'aws'   ? 'AWS account ID, 12 digits' :
        provider === 'azure' ? 'Azure subscription ID' : ''
      }>
        <input style={inp} value={accountId} disabled={isEdit} onChange={(e) => setAccountId(e.target.value)} required />
      </Field>

      {provider === 'gcp' && authMethod === 'wif' && (
        <div style={{
          padding: 16, border: `1px solid ${T.stroke}`, borderRadius: 6,
          background: T.bgTint, display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ fontWeight: 600, color: T.ink, fontSize: 13 }}>GCP Workload Identity Federation binding</div>
          <Field label="Project number" required hint="Numeric — find in GCP Console → Project info → Project number">
            <input style={inp} value={projectNumber} onChange={(e) => setProjectNumber(e.target.value)} pattern="\d+" required />
          </Field>
          <Field label="Workload Identity Pool ID" required>
            <input style={inp} value={poolId} onChange={(e) => setPoolId(e.target.value)} required />
          </Field>
          <Field label="Workload Identity Provider ID" required>
            <input style={inp} value={providerId} onChange={(e) => setProviderId(e.target.value)} required />
          </Field>
          <Field label="Deploy service account email" required hint="Must end with .iam.gserviceaccount.com">
            <input style={inp} value={deploySa} onChange={(e) => setDeploySa(e.target.value)}
                   placeholder="prove7-deploy@PROJECT.iam.gserviceaccount.com" required />
          </Field>
          <Field label="Audience (auto-computed)" readOnly hint="Validated server-side against the OIDC trust binding.">
            <input style={{ ...inp, background: T.bgAlt, color: T.text2 }} value={audience} readOnly />
          </Field>
        </div>
      )}

      {err && <Banner color={T.danger} bg={T.dangerBg}>{err}</Banner>}

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 12 }}>
        <button type="button" onClick={onClose} style={btn}>Cancel</button>
        <div style={{ flex: 1 }} />
        <button type="submit" disabled={submitting} style={btnPrimary}>
          {submitting ? (isEdit ? 'Saving…' : 'Binding…') : (isEdit ? 'Save & verify' : 'Bind cloud account')}
        </button>
      </div>
    </form>
  );
}

// ─── Verify result banner ────────────────────────────────────────────────────
function VerifyResultBanner({ result, onClose }) {
  const ok = result?.ok === true;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, maxWidth: 480, zIndex: 20,
      background: ok ? T.successBg : T.dangerBg,
      borderLeft: `3px solid ${ok ? T.success : T.danger}`,
      padding: '12px 16px', borderRadius: 6, color: ok ? T.success : T.danger, fontSize: 13,
      boxShadow: '0 8px 24px rgba(10,29,68,.16)',
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {ok ? 'Cloud account verified' : 'Verification failed'}
          </div>
          {result.reason && <div>{result.reason}</div>}
          {result.note   && <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>{result.note}</div>}
          {result.checks && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 11 }}>
              {Object.entries(result.checks).map(([k, v]) => (
                <li key={k}>{v ? '✓' : '✗'} {k}</li>
              ))}
            </ul>
          )}
        </div>
        <button onClick={onClose} style={btnSm}>Close</button>
      </div>
    </div>
  );
}

// ─── Slideout (anchored right, NOT a modal overlay) ──────────────────────────
function Slideout({ open, onClose, children }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(10,29,68,.18)', zIndex: 30,
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, maxWidth: '90vw',
        background: T.bgCard, borderLeft: `1px solid ${T.stroke}`, zIndex: 31,
        boxShadow: '-12px 0 32px rgba(10,29,68,.12)',
        display: 'flex', flexDirection: 'column',
      }}>
        {children}
      </div>
    </>
  );
}

// ─── Field primitives (V2 form discipline) ───────────────────────────────────
function Field({ label, hint, required, readOnly, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
        {label}{required && <span style={{ color: T.danger, marginLeft: 2 }}>*</span>}
        {readOnly && <span style={{ color: T.text3, marginLeft: 8, fontStyle: 'italic' }}>(read-only)</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: T.text2 }}>{hint}</div>}
    </div>
  );
}

// ─── Style primitives ────────────────────────────────────────────────────────
const inp = {
  fontFamily: 'inherit', fontSize: 13, padding: '7px 10px',
  border: `1px solid ${T.stroke}`, borderRadius: 4, background: '#fff', color: T.text,
  width: '100%', boxSizing: 'border-box',
};

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 4, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: `1px solid ${T.stroke}`, background: '#fff', color: T.ink,
};
const btnPrimary = { ...btn, background: T.action, border: `1px solid ${T.action}`, color: '#fff' };
const btnSm = { ...btn, padding: '4px 10px', fontSize: 12 };
const btnSmDanger = { ...btnSm, color: T.danger, border: `1px solid ${T.danger}` };

function Th({ children }) {
  return <th style={{
    textAlign: 'left', padding: '10px 12px', fontSize: 11,
    textTransform: 'uppercase', letterSpacing: '.06em',
    color: T.text2, fontWeight: 600, borderBottom: `1px solid ${T.stroke}`,
  }}>{children}</th>;
}
function Td({ children }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{children}</td>;
}
function Banner({ color, bg, children }) {
  return <div style={{
    background: bg, borderLeft: `3px solid ${color}`, padding: '10px 14px',
    borderRadius: 4, color, fontSize: 13, margin: '12px 0',
  }}>{children}</div>;
}

function ProviderPill({ provider }) {
  const colors = {
    gcp:   { fg: '#1f5fa6', bg: '#ecf3f8', mark: 'G' },
    aws:   { fg: '#7a4d00', bg: '#fff4d9', mark: 'A' },
    azure: { fg: '#1f5fa6', bg: '#ecf3f8', mark: 'Z' },
  };
  const c = colors[provider] || { fg: T.text2, bg: T.bgAlt, mark: '?' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontWeight: 600, fontSize: 12, padding: '2px 8px 2px 4px',
      borderRadius: 4, border: `1px solid ${c.fg}33`,
      background: c.bg, color: c.fg,
    }}>
      <span style={{
        width: 16, height: 16, display: 'grid', placeItems: 'center',
        background: c.fg, color: '#fff', borderRadius: 3, fontSize: 9, fontWeight: 800,
      }}>{c.mark}</span>
      {provider?.toUpperCase()}
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { fg: T.warn,    bg: T.warnBg,    label: 'Pending verify' },
    active:  { fg: T.success, bg: T.successBg, label: 'Active' },
    revoked: { fg: T.danger,  bg: T.dangerBg,  label: 'Revoked' },
    error:   { fg: T.danger,  bg: T.dangerBg,  label: 'Error' },
  };
  const c = map[status] || { fg: T.text2, bg: T.bgAlt, label: status || '—' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      border: `1px solid ${c.fg}33`,
    }}>{c.label}</span>
  );
}
